import React, { useState } from 'react';
import { usePolicy } from '../context/PolicyContext';
import { Policy } from '../types/policy';
import './Form.css';

const RetrievePolicy: React.FC = () => {
  const { getPolicy } = usePolicy();
  const [policyNumber, setPolicyNumber] = useState('');
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [error, setError] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPolicy(null);

    if (!policyNumber.trim()) {
      setError('Please enter a policy number');
      return;
    }

    const foundPolicy = getPolicy(policyNumber.trim());
    if (foundPolicy) {
      setPolicy(foundPolicy);
    } else {
      setError('Policy not found. Please check the policy number and try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const statusClasses: Record<string, string> = {
      active: 'status-active',
      cancelled: 'status-cancelled',
      expired: 'status-expired',
      reinstated: 'status-reinstated',
    };
    return statusClasses[status] || '';
  };

  return (
    <div className="form-container">
      <h2>Retrieve Insurance Policy</h2>

      <form onSubmit={handleSearch} className="form">
        <div className="form-group">
          <label htmlFor="policyNumber">Policy Number *</label>
          <input
            type="text"
            id="policyNumber"
            name="policyNumber"
            value={policyNumber}
            onChange={(e) => setPolicyNumber(e.target.value)}
            placeholder="Enter policy number (e.g., POL-1234567890-ABC123)"
            className={error ? 'error' : ''}
          />
          {error && <span className="error-text">{error}</span>}
        </div>

        <button type="submit" className="submit-button">Search Policy</button>
      </form>

      {policy && (
        <div className="policy-details">
          <h3>Policy Details</h3>
          <div className="details-grid">
            <div className="detail-item">
              <strong>Policy Number:</strong>
              <span>{policy.policyNumber}</span>
            </div>
            <div className="detail-item">
              <strong>Status:</strong>
              <span className={`status-badge ${getStatusBadge(policy.status)}`}>
                {policy.status.toUpperCase()}
              </span>
            </div>
            <div className="detail-item">
              <strong>Policy Holder:</strong>
              <span>{policy.policyHolderName}</span>
            </div>
            <div className="detail-item">
              <strong>Email:</strong>
              <span>{policy.policyHolderEmail}</span>
            </div>
            <div className="detail-item">
              <strong>Phone:</strong>
              <span>{policy.policyHolderPhone}</span>
            </div>
            <div className="detail-item">
              <strong>Policy Type:</strong>
              <span>{policy.policyType}</span>
            </div>
            <div className="detail-item">
              <strong>Coverage Amount:</strong>
              <span>${policy.coverageAmount.toLocaleString()}</span>
            </div>
            <div className="detail-item">
              <strong>Premium Amount:</strong>
              <span>${policy.premiumAmount.toLocaleString()}</span>
            </div>
            <div className="detail-item">
              <strong>Start Date:</strong>
              <span>{formatDate(policy.startDate)}</span>
            </div>
            <div className="detail-item">
              <strong>End Date:</strong>
              <span>{formatDate(policy.endDate)}</span>
            </div>
            {policy.cancellationDate && (
              <>
                <div className="detail-item">
                  <strong>Cancellation Date:</strong>
                  <span>{formatDate(policy.cancellationDate)}</span>
                </div>
                <div className="detail-item">
                  <strong>Cancellation Reason:</strong>
                  <span>{policy.cancellationReason}</span>
                </div>
              </>
            )}
          </div>

          {policy.incidents.length > 0 && (
            <div className="incidents-section">
              <h4>Incidents/Claims</h4>
              <div className="incidents-list">
                {policy.incidents.map((incident) => (
                  <div key={incident.id} className="incident-item">
                    <div><strong>Date:</strong> {formatDate(incident.incidentDate)}</div>
                    <div><strong>Description:</strong> {incident.description}</div>
                    <div><strong>Claim Amount:</strong> ${incident.claimAmount.toLocaleString()}</div>
                    <div><strong>Status:</strong> {incident.status}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RetrievePolicy;


